import React from 'react';
import { Link } from 'react-router-dom';
import { Target, Users, Lightbulb, Trophy, ArrowRight, CheckCircle } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';

const BeImmortal = () => {
  const experiences = [
    {
      icon: Users,
      title: 'Leadership Training',
      description: 'Public speaking, conflict resolution, teamwork, accountability'
    },
    {
      icon: Trophy,
      title: 'Character Development',
      description: 'Discipline, confidence, mental toughness, time management'
    },
    {
      icon: Target,
      title: 'Life Readiness Skills',
      description: 'Professional etiquette, interview skills, budgeting basics, personal responsibility'
    },
    {
      icon: Lightbulb,
      title: 'Empowerment Workshops',
      description: 'Vision-setting, identity development, purpose discovery'
    }
  ];

  const outcomes = [
    'Stronger confidence',
    'Better communication',
    'Improved decision-making',
    'Leadership qualities that last a lifetime'
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-black text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-block mb-6 px-6 py-2 bg-[#E6B933]/20 rounded-full border border-[#E6B933]">
              <p className="text-[#E6B933] font-semibold">Life Skills & Leadership</p>
            </div>
            <h1 className="text-6xl md:text-7xl font-bold mb-6">
              Be <span className="text-[#E6B933]">Immortal</span>
            </h1>
            <p className="text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed mb-8">
              Build the mindset, discipline, and character required to thrive in school, sports, and life.
            </p>
            <a href="mailto:thewhymefoundation@gmail.com?subject=Join Be Immortal - Life Skills & Leadership&body=Hi, I'm interested in joining the Be Immortal program. Please send me more information about enrollment.%0D%0A%0D%0AName:%0D%0AAge:%0D%0ASchool:%0D%0APhone:%0D%0AEmail:%0D%0A">
              <Button className="bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
                Join Be Immortal
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our <span className="text-[#E6B933]">Mission</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              To develop confident, resilient leaders by teaching essential life skills, emotional intelligence, communication, and real-world decision-making.
            </p>
          </div>
        </div>
      </section>

      {/* What Students Experience */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              What Students <span className="text-[#E6B933]">Experience</span>
            </h2>
            <p className="text-xl text-gray-600">Comprehensive leadership development that transforms lives</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {experiences.map((exp, index) => {
              const IconComponent = exp.icon;
              return (
                <Card key={index} className="p-8 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-l-4 border-[#E6B933]">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-[#E6B933]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <IconComponent className="text-[#E6B933]" size={28} />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{exp.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{exp.description}</p>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="p-12 bg-gradient-to-br from-gray-50 to-white border-2 border-[#E6B933]">
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">
              Who It's <span className="text-[#E6B933]">For</span>
            </h2>
            <p className="text-xl text-gray-600 text-center leading-relaxed">
              Middle school & high school students ready to grow, lead, and elevate their future.
            </p>
          </Card>
        </div>
      </section>

      {/* Program Outcomes */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Program <span className="text-[#E6B933]">Outcomes</span>
            </h2>
            <p className="text-xl text-gray-600">Students leave Be Immortal with:</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {outcomes.map((outcome, index) => (
              <Card key={index} className="p-6 hover:shadow-lg transition-all duration-300">
                <div className="flex items-center space-x-4">
                  <CheckCircle className="text-[#E6B933] flex-shrink-0" size={28} />
                  <p className="text-lg font-semibold text-gray-900">{outcome}</p>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-black text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#E6B933] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-5xl font-bold mb-6">
            Be Immortal. <span className="text-[#E6B933]">Lead with Purpose.</span> Live with Impact.
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join a community of young leaders building the skills that last a lifetime.
          </p>
          <a href="mailto:thewhymefoundation@gmail.com?subject=Join Be Immortal - Life Skills & Leadership&body=Hi, I'm interested in joining the Be Immortal program. Please send me more information about enrollment.%0D%0A%0D%0AName:%0D%0AAge:%0D%0ASchool:%0D%0APhone:%0D%0AEmail:%0D%0A">
            <Button className="bg-[#E6B933] hover:bg-[#D4A01F] text-black font-bold px-8 py-6 text-lg transition-all duration-300 hover:scale-105">
              Get Started Today
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default BeImmortal;
